import { useState } from "react";
import { Download, FileText, Building2, Sofa, Boxes, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal, Label } from "@/components/site/Motion";
import { CTASection } from "@/components/site/Sections";
import { useSettings } from "@/hooks/useSettings";
import { Input } from "@/components/ui/input";

const SHEETS = [
  { icon: Building2, title: "UYI Construction", desc: "Residential & commercial building capabilities, process and past projects." },
  { icon: Sofa, title: "UYI Interior", desc: "Interior design services, material palettes and signature spaces." },
  { icon: Boxes, title: "UYI Furniture", desc: "Bespoke furniture catalogue, finishes and made-to-order options." },
];

export default function Brochure() {
  const s = useSettings();
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const triggerDownload = () => {
    if (s.brochure_url) {
      window.open(s.brochure_url, "_blank");
    } else {
      toast.info("Our brochure is being finalised — our team will share it with you on the details provided.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number to download.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", {
        ...form,
        service: "Brochure Download",
        message: "Requested company brochure download.",
      });
      toast.success("Thank you! Downloading your brochure…");
      triggerDownload();
      setForm({ name: "", phone: "", email: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="brochure-page">
      {/* Lead-capture form ABOVE the banner */}
      <section className="pt-32 lg:pt-36 pb-14 bg-navy-deep grain relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-5xl px-5 lg:px-8">
          <div className="text-center">
            <Label>Instant access</Label>
            <h1 className="mt-4 font-display text-3xl md:text-5xl text-white tracking-tight">
              Download the UYI company brochure
            </h1>
            <p className="mt-4 text-white/60 max-w-xl mx-auto">
              Fill in your details below and your brochure download will begin
              right away.
            </p>
          </div>

          <form
            onSubmit={submit}
            data-testid="brochure-form"
            className="mt-10 rounded-sm border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 lg:p-8 grid gap-4 md:grid-cols-4 items-end"
          >
            <div className="md:col-span-1">
              <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/40">Name *</label>
              <Input data-testid="brochure-name" value={form.name} onChange={set("name")} placeholder="Your name" className="rounded-sm bg-white/5 border-white/10 text-white" />
            </div>
            <div className="md:col-span-1">
              <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/40">Phone *</label>
              <Input data-testid="brochure-phone" value={form.phone} onChange={set("phone")} placeholder="+91" className="rounded-sm bg-white/5 border-white/10 text-white" />
            </div>
            <div className="md:col-span-1">
              <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/40">Email</label>
              <Input data-testid="brochure-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="rounded-sm bg-white/5 border-white/10 text-white" />
            </div>
            <button
              type="submit"
              data-testid="brochure-download"
              disabled={loading}
              className="md:col-span-1 inline-flex items-center justify-center gap-2 rounded-sm bg-gold px-6 py-2.5 text-navy font-medium hover:bg-white transition-colors duration-300 disabled:opacity-60 h-10"
            >
              {loading ? "Please wait…" : (<><Download className="h-4 w-4" /> Download</>)}
            </button>
          </form>
        </div>
      </section>

      {/* Banner + details below the form */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-28">
        <div className="grid gap-14 md:grid-cols-12 items-center">
          <div className="md:col-span-6">
            <div className="relative rounded-sm border border-navy/10 bg-navy text-white p-10 lg:p-14 grain overflow-hidden">
              <div className="relative z-10">
                <FileText className="h-14 w-14 text-gold" strokeWidth={1.2} />
                <h2 className="mt-6 font-display text-3xl md:text-4xl">{s.brochure_title || "United Youngs India — Company Profile"}</h2>
                <p className="mt-4 text-white/60 leading-relaxed">
                  {s.brochure_description || "A complete overview of our services, philosophy and portfolio — spanning construction, interior and bespoke furniture."}
                </p>
                <button
                  onClick={submit}
                  data-testid="brochure-download-secondary"
                  className="mt-8 inline-flex items-center gap-3 rounded-sm bg-gold px-8 py-4 text-navy font-medium hover:bg-white transition-colors duration-300"
                >
                  <Send className="h-5 w-5" /> Fill the form to download
                </button>
              </div>
            </div>
          </div>
          <div className="md:col-span-6 space-y-5">
            <Label>Inside the brochure</Label>
            {SHEETS.map((sh, i) => {
              const Icon = sh.icon;
              return (
                <Reveal key={sh.title} delay={i * 0.08}>
                  <div className="flex gap-5 rounded-sm border border-navy/10 bg-white p-6">
                    <Icon className="h-8 w-8 text-gold shrink-0" strokeWidth={1.3} />
                    <div>
                      <h3 className="font-display text-xl text-navy">{sh.title}</h3>
                      <p className="mt-1 text-sm text-navy/55 leading-relaxed">{sh.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
